/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { gamificationApi } from '../../services/gamificationApi';
import type { Achievement, Challenge, LeaderboardEntry } from '../../types';

interface StreakInfo {
  current: number;
  longest: number;
  lastActiveDate: string;
}

interface GamificationState {
  badges: Achievement[];
  challenges: Challenge[];
  leaderboard: LeaderboardEntry[];
  streak: StreakInfo | null;
  isBadgesLoading: boolean;
  isChallengesLoading: boolean;
  isLeaderboardLoading: boolean;
  isStreakLoading: boolean;
  error: string | null;
}

const initialState: GamificationState = {
  badges: [],
  challenges: [],
  leaderboard: [],
  streak: null,
  isBadgesLoading: false,
  isChallengesLoading: false,
  isLeaderboardLoading: false,
  isStreakLoading: false,
  error: null,
};

export const fetchBadges = createAsyncThunk(
  'gamification/fetchBadges',
  async (_, { rejectWithValue }) => {
    try {
      return await gamificationApi.getBadges();
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load badges');
    }
  }
);

export const fetchChallenges = createAsyncThunk(
  'gamification/fetchChallenges',
  async (_, { rejectWithValue }) => {
    try {
      return await gamificationApi.getChallenges();
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load challenges');
    }
  }
);

export const joinChallenge = createAsyncThunk(
  'gamification/joinChallenge',
  async (challengeId: string, { rejectWithValue }) => {
    try {
      return await gamificationApi.joinChallenge(challengeId);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to join challenge');
    }
  }
);

export const fetchLeaderboard = createAsyncThunk(
  'gamification/fetchLeaderboard',
  async (period: 'weekly' | 'monthly' | 'all_time' = 'weekly', { rejectWithValue }) => {
    try {
      return await gamificationApi.getLeaderboard(period);
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load leaderboard');
    }
  }
);

export const fetchStreak = createAsyncThunk(
  'gamification/fetchStreak',
  async (_, { rejectWithValue }) => {
    try {
      return await gamificationApi.getStreak();
    } catch (error: any) {
      return rejectWithValue(error.detail || 'Failed to load streak');
    }
  }
);

const gamificationSlice = createSlice({
  name: 'gamification',
  initialState,
  reducers: {
    clearGamificationError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch Badges
    builder.addCase(fetchBadges.pending, (state) => {
      state.isBadgesLoading = true;
      state.error = null;
    });
    builder.addCase(fetchBadges.fulfilled, (state, action) => {
      state.badges = action.payload;
      state.isBadgesLoading = false;
    });
    builder.addCase(fetchBadges.rejected, (state, action) => {
      state.isBadgesLoading = false;
      state.error = action.payload as string;
    });

    // Fetch Challenges
    builder.addCase(fetchChallenges.pending, (state) => {
      state.isChallengesLoading = true;
      state.error = null;
    });
    builder.addCase(fetchChallenges.fulfilled, (state, action) => {
      state.challenges = action.payload;
      state.isChallengesLoading = false;
    });
    builder.addCase(fetchChallenges.rejected, (state, action) => {
      state.isChallengesLoading = false;
      state.error = action.payload as string;
    });

    // Join Challenge
    builder.addCase(joinChallenge.fulfilled, (state, action) => {
      const idx = state.challenges.findIndex((c) => c.id === action.payload.id);
      if (idx !== -1) {
        state.challenges[idx] = action.payload;
      }
    });

    // Fetch Leaderboard
    builder.addCase(fetchLeaderboard.pending, (state) => {
      state.isLeaderboardLoading = true;
      state.error = null;
    });
    builder.addCase(fetchLeaderboard.fulfilled, (state, action) => {
      state.leaderboard = action.payload;
      state.isLeaderboardLoading = false;
    });
    builder.addCase(fetchLeaderboard.rejected, (state, action) => {
      state.isLeaderboardLoading = false;
      state.error = action.payload as string;
    });

    // Fetch Streak
    builder.addCase(fetchStreak.pending, (state) => {
      state.isStreakLoading = true;
    });
    builder.addCase(fetchStreak.fulfilled, (state, action) => {
      state.streak = action.payload;
      state.isStreakLoading = false;
    });
    builder.addCase(fetchStreak.rejected, (state, action) => {
      state.isStreakLoading = false;
      state.error = action.payload as string;
    });
  },
});

export const { clearGamificationError } = gamificationSlice.actions;
export default gamificationSlice.reducer;
